import BigNumber from 'bignumber.js'
import {
  SerializedPool,
  SerializedCakeVault,
  DeserializedCakeVault,
  SerializedLockedCakeVault,
  VaultKey,
} from 'state/types'
import { deserializeToken } from '@pancakeswap/token-lists'
import { BIG_ZERO } from '@pancakeswap/utils/bigNumber'
import { DeserializedPool, IsCakeVault, IsLocked } from '@pancakeswap/pools'
import { safeGetAddress } from 'utils'
import { convertSharesToCake } from 'views/Pools/helpers'
import { Token } from '@pancakeswap/sdk'

type UserData =
  | DeserializedPool<Token>['userData']
  | {
    allowance: number | string
    stakingTokenBalance: number | string
    stakedBalance: number | string
    pendingReward: number | string
  }

export const transformUserData = (userData: UserData) => {
  return {
    allowance: userData ? new BigNumber(userData.allowance) : BIG_ZERO,
    stakingTokenBalance: userData ? new BigNumber(userData.stakingTokenBalance) : BIG_ZERO,
    stakedBalance: userData ? new BigNumber(userData.stakedBalance) : BIG_ZERO,
    pendingReward: userData ? new BigNumber(userData.pendingReward) : BIG_ZERO,
  }
}

const transformProfileRequirement = (profileRequirement?: { required: boolean; thresholdPoints: string }) => {
  return profileRequirement
    ? {
      required: profileRequirement.required,
      thresholdPoints: profileRequirement.thresholdPoints
        ? new BigNumber(profileRequirement.thresholdPoints)
        : BIG_ZERO,
    }
    : undefined
}

export const transformPool = (pool: SerializedPool): DeserializedPool<Token> => {
  const {
    totalStaked,
    stakingLimit,
    numberSecondsForUserLimit,
    userData,
    stakingToken,
    earningToken,
    profileRequirement,
    startTimestamp,
    ...rest
  } = pool

  return {
    ...rest,
    startTimestamp,
    profileRequirement: transformProfileRequirement(profileRequirement),
    stakingToken: deserializeToken(stakingToken),
    earningToken: deserializeToken(earningToken),
    userData: transformUserData(userData),
    totalStaked: new BigNumber(totalStaked ?? '0'),
    stakingLimit: new BigNumber(stakingLimit ?? '0'),
    stakingLimitEndTimestamp: (numberSecondsForUserLimit ?? 0) + (startTimestamp ?? 0),
  }
}

export const transformVault = (vaultKey: VaultKey, vault: SerializedCakeVault): DeserializedCakeVault => {
  const {
    isFinished,
    totalShares: totalSharesAsString,
    totalStakedAmount: totalStakedAsString,
    pricePerFullShare: pricePerFullShareAsString,
    pendingRewards: pendingRewardsAsString,
    fees: { performanceFee, withdrawalFee, withdrawalFeePeriod },
    userData: {
      isLoading,
      userShares: userSharesAsString,
      lastUserActionAmount: cakeAtLastUserActionAsString,
      lastDepositedTime,
      lastUserActionTime,
      pendingProfit: pendingProfitAsString
    },
  } = vault

  const totalShares = totalSharesAsString ? new BigNumber(totalSharesAsString) : BIG_ZERO
  const totalStakedAmount = totalStakedAsString ? new BigNumber(totalStakedAsString) : BIG_ZERO
  const pricePerFullShare = pricePerFullShareAsString ? new BigNumber(pricePerFullShareAsString) : BIG_ZERO
  const pendingRewards = pendingRewardsAsString ? new BigNumber(pendingRewardsAsString) : BIG_ZERO
  const userShares = new BigNumber(userSharesAsString)
  const lastUserActionAmount = new BigNumber(cakeAtLastUserActionAsString)
  const pendingProfit = pendingProfitAsString ? new BigNumber(pendingProfitAsString) : BIG_ZERO
  let userDataExtra
  let publicDataExtra
  if (IsLocked(vaultKey)) {
    const {
      totalCakeInVault: totalCakeInVaultAsString,
      totalLockedAmount: totalLockedAmountAsString,
      totalBoostDebt: totalBoostAsString,
      userData: {
        userBoostedShare: userBoostedShareAsString,
        lockEndTime,
        lockStartTime,
        locked,
        lockedAmount: lockedAmountAsString,
        currentOverdueFee: currentOverdueFeeAsString,
        currentPerformanceFee: currentPerformanceFeeAsString,
      },
    } = vault as SerializedLockedCakeVault
    const totalCakeInVault = totalCakeInVaultAsString && new BigNumber(totalCakeInVaultAsString).gt(0) ? new BigNumber(totalCakeInVaultAsString).plus(IsCakeVault(vaultKey) ? pendingRewards : BIG_ZERO) : BIG_ZERO
    // const totalCakeInVault = new BigNumber(totalCakeInVaultAsString)
    const totalBoostedAmount = new BigNumber(totalBoostAsString ?? '0')
    const totalLockedAmount = new BigNumber(totalLockedAmountAsString ?? '0')
    const lockedAmount = new BigNumber(lockedAmountAsString)
    const userBoostedShare = new BigNumber(userBoostedShareAsString)
    const currentOverdueFee = currentOverdueFeeAsString ? new BigNumber(currentOverdueFeeAsString) : BIG_ZERO
    const currentPerformanceFee = currentPerformanceFeeAsString
      ? new BigNumber(currentPerformanceFeeAsString)
      : BIG_ZERO

    const balance = convertSharesToCake(
      userShares,
      pricePerFullShare,
      undefined,
      undefined,
      currentOverdueFee.plus(currentPerformanceFee).plus(IsCakeVault(vaultKey) ? userBoostedShare : BIG_ZERO),
    )
    userDataExtra = {
      lockEndTime,
      lockStartTime,
      locked,
      lockedAmount,
      userBoostedShare,
      currentOverdueFee,
      currentPerformanceFee,
      balance,
    }
    publicDataExtra = { totalLockedAmount, totalCakeInVault, totalBoostedAmount, totalStakedAmount }
  } else {
    const balance = convertSharesToCake(userShares, pricePerFullShare)
    const { cakeAsBigNumber } = convertSharesToCake(totalShares, pricePerFullShare)
    userDataExtra = { balance }
    publicDataExtra = { totalCakeInVault: cakeAsBigNumber, totalStakedAmount }
  }

  const performanceFeeAsDecimal = performanceFee && performanceFee / 100

  return {
    isFinished,
    totalShares,
    pricePerFullShare,
    pendingRewards,
    ...publicDataExtra,
    fees: { performanceFee, withdrawalFee, withdrawalFeePeriod, performanceFeeAsDecimal },
    userData: {
      isLoading,
      userShares,
      lastUserActionAmount,
      lastDepositedTime,
      lastUserActionTime,
      pendingProfit,
      ...userDataExtra,
    },
  }
}

export const getTokenPricesFromFarm = (
  farms: {
    quoteToken: { address: string }
    token: { address: string }
    quoteTokenPriceBusd: string
    tokenPriceBusd: string
  }[],
) => {
  return farms.reduce((prices, farm) => {
    const quoteTokenAddress = safeGetAddress(farm.quoteToken.address)
    const tokenAddress = safeGetAddress(farm.token.address)
    /* eslint-disable no-param-reassign */
    if (quoteTokenAddress && !prices[quoteTokenAddress]) {
      prices[quoteTokenAddress] = new BigNumber(farm.quoteTokenPriceBusd).toNumber()
    }
    if (tokenAddress && !prices[tokenAddress]) {
      prices[tokenAddress] = new BigNumber(farm.tokenPriceBusd).toNumber()
    }
    /* eslint-enable no-param-reassign */
    return prices
  }, {})
}
